import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import React, { JSX, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { fieldProps, useValidatedForm } from '../../../library/formValidation';
import { useApiFormError } from '../../../library/useApiFormError';
import { ClinicFormValues, IClinic, IClinicDomain } from '../../../library/models/clinic';
import ResourcesIds from '../../../resources/ResourcesIds';
import { ClinicDomains } from './ClinicDomains';
import { createClinicSchema } from './clinicSchema';
import { useIsCompact } from '../../../library/useIsCompact';
import { dialogActionsStyles, dialogFormStyles, dialogTitleStyles, fieldsBoxStyles, formAlertStyles } from './clinicFormDialog.styles';

interface ClinicFormDialogProps {
    isOpen: boolean;
    /** The clinic being edited, or null to create a new one. */
    clinic: IClinic | null;
    onClose: () => void;
    /** Rejects with the API error, which this dialog maps back onto the fields. */
    onSubmit: (values: ClinicFormValues) => Promise<unknown>;
    /** A domain was added, removed or re-pointed. Only ever called while editing. */
    onDomainsChanged: (domains: IClinicDomain[]) => void;
}

/** The API's own defaults, so a new clinic starts on the hours it would get anyway. */
const DEFAULT_VALUES: ClinicFormValues = {
    slug: '',
    displayName: '',
    hostname: '',
    senderEmail: '',
};

/**
 * Blank hostname when editing, always: an existing clinic's domains are not carried by this
 * form. See the `ClinicDomains` panel, which manages them one at a time. The sender address,
 * by contrast, is seeded -- it is saved with the form, so a blank here would clear it.
 */
const toFormValues = (clinic: IClinic | null): ClinicFormValues => clinic
    ? {
        slug: clinic.slug,
        displayName: clinic.displayName,
        hostname: '',
        senderEmail: clinic.senderEmail ?? '',
    }
    : DEFAULT_VALUES;

/**
 * Create and edit in one dialog rather than a route, so the list stays mounted behind it
 * and the row's own record seeds the form -- editing never needs to re-fetch a clinic by
 * id, which matters here because the API answers an unknown id with a 500 rather than a
 * 404.
 *
 * Uses no router hooks, which is what lets it be rendered on its own in a test.
 */
export const ClinicFormDialog = (props: ClinicFormDialogProps): JSX.Element => {
    const { t: translate } = useTranslation();
    const isCompact = useIsCompact();
    const isEdit = props.clinic !== null;

    const schema = React.useMemo(() => createClinicSchema(translate), [translate]);

    const {
        register, handleSubmit, reset, setError, formState: { errors, isValid, isSubmitting },
    } = useValidatedForm<ClinicFormValues>(schema, { defaultValues: DEFAULT_VALUES });

    const knownFields: string[] = React.useMemo(() => Object.keys(schema.fields), [schema]);
    const { formError, report, clear } = useApiFormError<ClinicFormValues>(
        setError, knownFields, translate, ResourcesIds.CLINIC_SAVE_ERROR);

    // Reseed every time the dialog opens, so it never shows the previous clinic's values.
    useEffect(() => {
        if (props.isOpen) {
            reset(toFormValues(props.clinic));
            clear();
        }
    }, [props.isOpen, props.clinic, reset, clear]);

    const onValid = async (values: ClinicFormValues): Promise<void> => {
        clear();

        try {
            await props.onSubmit(values);
        } catch (error: unknown) {
            report(error);
        }
    };

    return (
        <Dialog open={props.isOpen} onClose={props.onClose} maxWidth="sm" fullWidth fullScreen={isCompact}>
            <DialogTitle sx={dialogTitleStyles}>
                {translate(isEdit ? ResourcesIds.EDIT_CLINIC_TITLE : ResourcesIds.CREATE_CLINIC_TITLE)}
            </DialogTitle>

            <Box component="form" onSubmit={handleSubmit(onValid)} noValidate sx={dialogFormStyles}>
                <DialogContent>
                    {formError && <Alert severity="error" sx={formAlertStyles}>{formError}</Alert>}

                    <Box sx={fieldsBoxStyles}>
                        <TextField
                            {...register('displayName')}
                            fullWidth
                            autoFocus
                            label={translate(ResourcesIds.CLINIC_DISPLAY_NAME_LABEL)}
                            {...fieldProps(errors, 'displayName')}
                        />

                        <TextField
                            {...register('slug')}
                            fullWidth
                            // The slug identifies the clinic and the API will not change
                            // it, so editing shows it without offering to.
                            disabled={isEdit}
                            label={translate(ResourcesIds.CLINIC_SLUG_LABEL)}
                            {...fieldProps(errors, 'slug', translate(
                                isEdit ? ResourcesIds.CLINIC_SLUG_IMMUTABLE_HELPER : ResourcesIds.CLINIC_SLUG_HELPER
                            ))}
                        />

                        {/*
                          * Creating takes one hostname, in the same request as the clinic --
                          * a refused one fails the whole creation rather than leaving a
                          * clinic behind without the domain it was created for. An existing
                          * clinic gets the panel instead: its domains are a list, and each
                          * change to one takes effect on its own.
                          */}
                        {isEdit && props.clinic ? (
                            <>
                                <ClinicDomains
                                    clinicId={props.clinic.id}
                                    domains={props.clinic.domains}
                                    onChanged={props.onDomainsChanged}
                                />

                                {/*
                                  * Edit only: the address has to be on a domain already
                                  * authenticated in Brevo, which cannot be true of a clinic
                                  * that does not exist yet. Saved with the form, unlike the
                                  * domains above it.
                                  */}
                                <TextField
                                    {...register('senderEmail')}
                                    fullWidth
                                    type="email"
                                    label={translate(ResourcesIds.CLINIC_SENDER_EMAIL_LABEL)}
                                    {...fieldProps(errors, 'senderEmail',
                                        translate(ResourcesIds.CLINIC_SENDER_EMAIL_HINT))}
                                />
                            </>
                        ) : (
                            <TextField
                                {...register('hostname')}
                                fullWidth
                                label={translate(ResourcesIds.CLINIC_DOMAIN_LABEL)}
                                {...fieldProps(errors, 'hostname',
                                    translate(ResourcesIds.CLINIC_DOMAIN_HELPER))}
                            />
                        )}

                    </Box>
                </DialogContent>

                <DialogActions sx={dialogActionsStyles}>
                    <Button onClick={props.onClose} variant="outlined" disabled={isSubmitting}>
                        {translate(ResourcesIds.CANCEL_TEXT)}
                    </Button>
                    <Button type="submit" variant="contained" disabled={!isValid || isSubmitting}>
                        {translate(ResourcesIds.SAVE_CHANGES)}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    );
};
